import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * One size card of the "Supply cards" section. Adds its variant to the cart through the same
 * events the theme's own product form uses, so the cart drawer, cart bubble and live regions update.
 * Without JavaScript the form posts to the cart normally.
 */
class NfSupplyCard extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-nf-supply-form]');
    this.input = this.querySelector('.nf-qty__input');
    this.error = this.querySelector('.nf-card__error');
    this.button = this.querySelector('button[type="submit"]');

    this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.step(1));
    this.input?.addEventListener('change', () => this.step(0));
    this.form?.addEventListener('submit', (event) => this.submit(event));
  }

  step(delta) {
    if (!this.input) return;
    const next = Math.max(1, (parseInt(this.input.value, 10) || 1) + delta);
    this.input.value = String(next);
  }

  showError(message) {
    if (!this.error) return;
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  async refreshCart() {
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }
    const response = await fetch(`${Theme.routes.cart_url}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  async submit(event) {
    event.preventDefault();
    if (!this.form || this.button?.disabled) return;

    this.showError('');
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');

    const formData = new FormData(this.form);
    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const itemCount = Number(formData.get('quantity')) || 1;
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity: itemCount }],
        promise: deferred.promise,
      })
    );

    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      }).then((r) => r.json());

      if (response.status) {
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID', detail: { description: response.description, errors: response.errors } }));
        this.showError(response.message || response.description || '');
        const cart = await this.refreshCart();
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items, source: 'nf-supply-card', sourceId: this.id || 'nf-supply-card', itemCount } });
        return;
      }

      const cart = await this.refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'nf-supply-card', sourceId: this.id || 'nf-supply-card', itemCount, sections: response.sections, didError: false },
      });
      this.button.dataset.added = 'true';
      setTimeout(() => this.button?.removeAttribute('data-added'), 1200);
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.showError(error?.message || '');
    } finally {
      this.button.disabled = false;
      this.button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('nf-supply-card')) customElements.define('nf-supply-card', NfSupplyCard);
